/**
 * ContractorQuickSetupModal - Gold-Standard First-Login Setup
 * 
 * Single-screen modal for new contractors to enter essential business details.
 * Uses structured address fields as the canonical source of truth.
 * 
 * Required: Phone number, Street address, City, State, ZIP
 * Optional: Business name, Business hours, Tax rate
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Buildings, Clock, Percent, Phone, MapPin } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Storage key for 24h snooze
const SNOOZE_KEY = "contractor_setup_snoozed_at";

// Check if snoozed (within 24 hours)
const isSnoozed = () => {
  const snoozedAt = localStorage.getItem(SNOOZE_KEY);
  if (!snoozedAt) return false;
  const snoozedTime = parseInt(snoozedAt, 10);
  const now = Date.now();
  const twentyFourHours = 24 * 60 * 60 * 1000;
  return now - snoozedTime < twentyFourHours;
};

// Set snooze
const setSnooze = () => {
  localStorage.setItem(SNOOZE_KEY, Date.now().toString());
};

// Clear snooze (after completing setup)
const clearSnooze = () => {
  localStorage.removeItem(SNOOZE_KEY);
};

// Validate phone number (basic: must have at least 10 digits)
const validatePhone = (phone) => {
  if (!phone || !phone.trim()) {
    return { valid: false, error: "Phone number is required" };
  }
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 10) {
    return { valid: false, error: "Phone must have at least 10 digits" };
  }
  if (digits.length > 15) {
    return { valid: false, error: "Phone number is too long" };
  }
  return { valid: true, error: null };
};

// Validate ZIP code (5 or 9 digits)
const validateZip = (zip) => {
  if (!zip || !zip.trim()) {
    return { valid: false, error: "ZIP code is required" };
  }
  const cleanZip = zip.replace(/[\s-]/g, "");
  if (/^\d{5}$/.test(cleanZip) || /^\d{9}$/.test(cleanZip)) {
    return { valid: true, error: null };
  }
  return { valid: false, error: "ZIP must be 5 digits (02101) or 9 digits" };
};

export default function ContractorQuickSetupModal({ isOpen, onClose, onComplete, initialData }) {
  // Form state - structured fields
  const [phone, setPhone] = useState("");
  const [addressStreet, setAddressStreet] = useState("");
  const [addressCity, setAddressCity] = useState("");
  const [addressState, setAddressState] = useState("");
  const [addressZip, setAddressZip] = useState("");
  const [businessHours, setBusinessHours] = useState({
    start: "08:00",
    end: "17:00",
  });
  const [taxRate, setTaxRate] = useState(0);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Initialize form with existing data
  useEffect(() => {
    if (initialData) {
      // Phone
      if (initialData.phone) {
        setPhone(initialData.phone);
      }
      // Structured address fields
      if (initialData.address_street) {
        setAddressStreet(initialData.address_street);
      }
      if (initialData.address_city) {
        setAddressCity(initialData.address_city);
      }
      if (initialData.address_state) {
        setAddressState(initialData.address_state);
      }
      if (initialData.address_zip) {
        setAddressZip(initialData.address_zip);
      }
      // Business hours
      if (initialData.business_hours) {
        setBusinessHours({
          start: initialData.business_hours.start || "08:00",
          end: initialData.business_hours.end || "17:00",
        });
      }
      // Tax rate
      if (typeof initialData.tax_rate_percent === "number") {
        setTaxRate(initialData.tax_rate_percent);
      }
    }
  }, [initialData]);

  // Validate all required fields
  const validateForm = () => {
    const errors = {};
    
    // Phone validation
    const phoneValidation = validatePhone(phone);
    if (!phoneValidation.valid) {
      errors.phone = phoneValidation.error;
    }
    
    // Street validation
    if (!addressStreet.trim()) {
      errors.street = "Street address is required";
    }
    
    // City validation
    if (!addressCity.trim()) {
      errors.city = "City is required";
    }
    
    // State validation
    if (!addressState.trim()) {
      errors.state = "State is required";
    }
    
    // ZIP validation
    const zipValidation = validateZip(addressZip);
    if (!zipValidation.valid) {
      errors.zip = zipValidation.error;
    }
    
    return errors;
  };

  // Check if form is valid for enabling Save button
  const formErrors = validateForm();
  const canSave = Object.keys(formErrors).length === 0;

  // Handle Save & Continue
  const handleSave = async (e) => {
    e?.preventDefault();
    
    // Clear previous errors
    setFieldErrors({});
    
    // Validate all fields
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem("auth_token");
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/contractor/settings`, {
        method: "PATCH",
        headers,
        credentials: "include",
        body: JSON.stringify({
          phone: phone.trim(),
          address_street: addressStreet.trim(),
          address_city: addressCity.trim(),
          address_state: addressState.trim().toUpperCase(),
          address_zip: addressZip.trim(),
          business_hours: businessHours,
          tax_rate_percent: parseFloat(taxRate) || 0,
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to save settings");
      }

      // Clear snooze and notify parent
      clearSnooze();
      toast.success("Setup complete! You're ready to send invoices.");
      onComplete?.();
      onClose();
    } catch (err) {
      setFieldErrors({ submit: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Handle Skip (X button or Skip button)
  const handleSkip = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      // Record skip on backend
      await fetch(`${API}/contractor/skip-setup`, {
        method: "POST",
        headers,
        credentials: "include",
      });
    } catch (err) {
      console.error("Failed to record skip:", err);
    }

    // Set local snooze
    setSnooze();
    onClose();
  };

  // Clear field error on input change
  const clearError = (field) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        if (!open) handleSkip();
      }}
    >
      <DialogContent 
        data-testid="contractor-quick-setup-modal"
        className="sm:max-w-lg"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          handleSkip();
        }}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Buildings size={22} weight="bold" className="text-[#FF6600]" />
            Let's get you ready for your first invoice.
          </DialogTitle>
          <DialogDescription>
            60 seconds. You can change this anytime in Settings.
          </DialogDescription>
        </DialogHeader>
        
        <DialogBody>
          <form id="contractor-setup-form" onSubmit={handleSave} className="space-y-4">
            {/* Phone Number - REQUIRED */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone size={16} className="text-zinc-400" />
                <span>Business Phone</span>
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  clearError("phone");
                }}
                placeholder="(555) 123-4567"
                className={`h-11 border-2 ${fieldErrors.phone ? 'border-red-500' : ''}`}
                data-testid="setup-phone-input"
              />
              {fieldErrors.phone && (
                <p className="text-xs text-red-500" data-testid="setup-phone-error">
                  {fieldErrors.phone}
                </p>
              )}
            </div>

            {/* Business Address - REQUIRED */}
            <div className="space-y-3 pt-2">
              <Label className="flex items-center gap-2">
                <MapPin size={16} className="text-zinc-400" />
                <span>Business Address</span>
                <span className="text-red-500">*</span>
              </Label>
              
              {/* Street */}
              <div>
                <Input
                  id="street"
                  name="street"
                  value={addressStreet}
                  onChange={(e) => {
                    setAddressStreet(e.target.value);
                    clearError("street");
                  }}
                  placeholder="Street address"
                  className={`h-11 border-2 ${fieldErrors.street ? 'border-red-500' : ''}`}
                  data-testid="setup-street-input"
                />
                {fieldErrors.street && (
                  <p className="text-xs text-red-500 mt-1">{fieldErrors.street}</p>
                )}
              </div>
              
              {/* City, State, ZIP in a row */}
              <div className="grid grid-cols-6 gap-2">
                <div className="col-span-3">
                  <Input
                    id="city"
                    name="city"
                    value={addressCity}
                    onChange={(e) => {
                      setAddressCity(e.target.value);
                      clearError("city");
                    }}
                    placeholder="City"
                    className={`h-11 border-2 ${fieldErrors.city ? 'border-red-500' : ''}`}
                    data-testid="setup-city-input"
                  />
                  {fieldErrors.city && (
                    <p className="text-xs text-red-500 mt-1">{fieldErrors.city}</p>
                  )}
                </div>
                <div className="col-span-1">
                  <Input
                    id="state"
                    name="state"
                    value={addressState}
                    onChange={(e) => {
                      setAddressState(e.target.value.toUpperCase().slice(0, 2));
                      clearError("state");
                    }}
                    placeholder="ST"
                    maxLength={2}
                    className={`h-11 border-2 text-center uppercase ${fieldErrors.state ? 'border-red-500' : ''}`}
                    data-testid="setup-state-input"
                  />
                  {fieldErrors.state && (
                    <p className="text-xs text-red-500 mt-1">{fieldErrors.state}</p>
                  )}
                </div>
                <div className="col-span-2">
                  <Input
                    id="zip"
                    name="zip"
                    value={addressZip}
                    onChange={(e) => {
                      setAddressZip(e.target.value);
                      clearError("zip");
                    }}
                    placeholder="ZIP"
                    className={`h-11 border-2 ${fieldErrors.zip ? 'border-red-500' : ''}`}
                    data-testid="setup-zip-input"
                  />
                  {fieldErrors.zip && (
                    <p className="text-xs text-red-500 mt-1">{fieldErrors.zip}</p>
                  )}
                </div>
              </div>
              <p className="text-xs text-zinc-500">
                This appears on your invoices and estimates.
              </p>
            </div>

            {/* Business Hours - OPTIONAL */}
            <div className="pt-3 border-t border-zinc-200 space-y-2">
              <Label className="flex items-center gap-2 text-zinc-700">
                <Clock size={16} className="text-zinc-400" />
                <span>Business Hours</span>
                <span className="text-xs text-zinc-400 font-normal">(optional)</span>
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  type="time"
                  value={businessHours.start}
                  onChange={(e) => setBusinessHours({ ...businessHours, start: e.target.value })}
                  className="w-32 h-11 border-2"
                  data-testid="setup-hours-start"
                />
                <span className="text-zinc-400">to</span>
                <Input
                  type="time"
                  value={businessHours.end}
                  onChange={(e) => setBusinessHours({ ...businessHours, end: e.target.value })}
                  className="w-32 h-11 border-2"
                  data-testid="setup-hours-end"
                />
                <span className="text-xs text-zinc-400">Mon–Fri</span>
              </div>
            </div>

            {/* Tax Rate - OPTIONAL */}
            <div className="pt-3 border-t border-zinc-200 space-y-2">
              <Label className="flex items-center gap-2 text-zinc-700">
                <Percent size={16} className="text-zinc-400" />
                <span>Default Tax Rate</span>
                <span className="text-xs text-zinc-400 font-normal">(optional)</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="30"
                  step="0.25"
                  value={taxRate}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val) && val >= 0 && val <= 30) {
                      setTaxRate(val);
                    } else if (e.target.value === "") {
                      setTaxRate(0);
                    }
                  }}
                  className="w-24 h-11 border-2"
                  data-testid="setup-tax-rate"
                />
                <span className="text-zinc-500">%</span>
              </div>
              <p className="text-xs text-zinc-500">
                Applied to new invoices. 0% = no tax.
              </p>
            </div>

            {/* Submit Error */}
            {fieldErrors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600" data-testid="setup-submit-error">
                {fieldErrors.submit}
              </div>
            )}
          </form>
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleSkip}
            className="flex-1 sm:flex-none border-2"
            disabled={saving}
            data-testid="setup-skip-btn"
          >
            Skip for now
          </Button>
          <Button
            type="submit"
            form="contractor-setup-form"
            className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white disabled:opacity-50"
            disabled={!canSave || saving}
            data-testid="setup-save-btn"
          >
            {saving ? "Saving..." : "Save & Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * SetupReminderBanner - Dashboard banner to remind users to complete setup
 */
export function SetupReminderBanner({ onOpenSetup, onDismiss }) {
  return (
    <div 
      className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between gap-3"
      data-testid="setup-reminder-banner"
    >
      <div className="flex items-center gap-3">
        <Buildings size={20} className="text-amber-600 flex-shrink-0" weight="duotone" />
        <p className="text-sm text-amber-800">
          <span className="font-medium">Finish setup:</span> add your phone and address to send invoices.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={onDismiss}
          className="text-amber-600 hover:text-amber-800 hover:bg-amber-100 h-8 px-2"
          data-testid="dismiss-setup-banner-btn"
        >
          ✕
        </Button>
        <Button
          size="sm"
          onClick={onOpenSetup}
          className="bg-[#FF6600] hover:bg-[#CC5200] text-white h-8"
          data-testid="open-setup-btn"
        >
          Complete Setup
        </Button>
      </div>
    </div>
  );
}

// Export utilities for use elsewhere
export { isSnoozed, setSnooze, clearSnooze };
